import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreateApprenticeDto } from '../types/apprentice.types';
import { Standard } from '../types/standard.types';

export const AddApprentice: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [standards, setStandards] = useState<Standard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CreateApprenticeDto>({
    tenant_id: user?.tenant_id || '',
    first_name: '',
    last_name: '',
    email: '',
    canvas_user_id: '',
    uln: '',
    date_of_birth: '',
    standard_id: '',
    employer_name: '',
    start_date: '',
    planned_end_date: '',
  });

  const fetchStandards = useCallback(async () => {
    if (!user?.tenant_id) return;
    
    try {
      const response = await axios.get(`/api/standards/tenant/${user.tenant_id}`);
      setStandards(response.data);
    } catch (err) {
      console.error('Failed to load standards:', err);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    fetchStandards();
  }, [fetchStandards]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Remove empty optional fields
      const submitData: any = { ...formData };
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          delete submitData[key];
        }
      });

      await axios.post('/api/apprentices', submitData);
      navigate('/apprentices');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create apprentice');
    } finally {
      setLoading(false);
    }
  };

  const selectedStandard = standards.find(s => s.id === formData.standard_id);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Add New Apprentice</h2>
        <p className="text-gray-600">Register a new apprentice to the system</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        {/* Personal Information */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="uln" className="block text-sm font-medium text-gray-700 mb-2">
                Unique Learner Number (ULN)
              </label>
              <input
                type="text"
                id="uln"
                name="uln"
                value={formData.uln}
                onChange={handleChange}
                placeholder="e.g., 1234567890"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="canvas_user_id" className="block text-sm font-medium text-gray-700 mb-2">
                Canvas User ID
              </label>
              <input
                type="text"
                id="canvas_user_id"
                name="canvas_user_id"
                value={formData.canvas_user_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Apprenticeship Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Apprenticeship Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="standard_id" className="block text-sm font-medium text-gray-700 mb-2">
                Apprenticeship Standard <span className="text-red-500">*</span>
              </label>
              <select
                id="standard_id"
                name="standard_id"
                value={formData.standard_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a standard...</option>
                {standards.map((standard) => (
                  <option key={standard.id} value={standard.id}>
                    {standard.title} (Level {standard.level})
                  </option>
                ))}
              </select>
              {selectedStandard && (
                <p className="mt-2 text-sm text-gray-600">
                  Duration: {selectedStandard.duration_months} months | 
                  Reference: {selectedStandard.ifate_reference}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="employer_name" className="block text-sm font-medium text-gray-700 mb-2">
                Employer Name
              </label>
              <input
                type="text"
                id="employer_name"
                name="employer_name"
                value={formData.employer_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="planned_end_date" className="block text-sm font-medium text-gray-700 mb-2">
                Planned End Date
              </label>
              <input
                type="date"
                id="planned_end_date"
                name="planned_end_date"
                value={formData.planned_end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/apprentices')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Creating...' : 'Create Apprentice'}
          </button>
        </div>
      </form>
    </div>
  );
};
